import { CampaignFormData } from '../types/campaign';
import { validatePhoneNumber } from '../utils/common';

export type ValidationError = {
  field: keyof CampaignFormData;
  message: string;
};

export function useValidateCampaignForm(
  data: CampaignFormData,
  floorPrice: number = 50,
): ValidationError[] {
  const errors: ValidationError[] = [];

  // Name validation
  if (!data.name?.trim()) {
    errors.push({
      field: 'name',
      message: 'Campaign name is required',
    });
  }

  // Daily budget validation
  const dailyBudget = Number(data.daily_budget);
  if (isNaN(dailyBudget) || dailyBudget < floorPrice) {
    errors.push({
      field: 'daily_budget',
      message: `Daily budget must be at least $${floorPrice}`,
    });
  }

  // Total budget validation
  if (data.total_budget !== undefined && data.total_budget !== null) {
    const totalBudget = Number(data.total_budget);
    if (isNaN(totalBudget) || (totalBudget !== 0 && totalBudget < dailyBudget)) {
      errors.push({
        field: 'total_budget',
        message: 'Total budget must be greater than daily budget, unless it is 0.',
      });
    }
  }

  // Claim type validation
  if (!data.claim_type?.trim()) {
    errors.push({
      field: 'claim_type',
      message: 'Claim type is required',
    });
  }

  // Objective validation
  if (!data.objective?.trim()) {
    errors.push({
      field: 'objective',
      message: 'Objective is required',
    });
  }

  // Date and Time validation
  const startDate = data.start_at ? new Date(data.start_at) : null;
  const endDate = data.end_at ? new Date(data.end_at) : null;

  if (startDate && endDate && endDate <= startDate) {
    errors.push({
      field: 'end_at',
      message: 'End date must be after start date',
    });
  }

  if (data.start_time && data.end_time) {
    const startTime = data.start_time;
    const endTime = data.end_time;
    if (endTime <= startTime) {
      errors.push({
        field: 'end_time',
        message: 'End time must be after start time',
      });
    }
  }

  if (data.start_time && data.end_time) {
    const startTime = data.start_time;
    const endTime = data.end_time;
    if (endTime <= startTime) {
      errors.push({
        field: 'end_time',
        message: 'End time must be after start time',
      });
    }
  }

  // Phone number validation (consolidated)
  if (data.destination_phone_number) {
    const phoneNumber = data.destination_phone_number.trim();
    if (!phoneNumber) {
      errors.push({
        field: 'destination_phone_number',
        message: 'Destination phone number is required for this objective',
      });
    } else {
      const isValid = validatePhoneNumber(phoneNumber, "US");
      if (typeof isValid === "string") {
        errors.push({
          field: 'destination_phone_number',
          message: 'Please enter a valid international phone number (e.g., +1 555-123-4567)',
        });
      }
    }
  }

  return errors;
}
